import {
  PageContainer,
  ProForm,
  ProFormText,
  ProFormDateTimePicker,
  ProFormSwitch,
} from "@ant-design/pro-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getClubs } from "../../../api";
import { LeagueGamesAPI } from "../../../api/leagueGames";
import { useNavigate } from "react-router-dom";
import * as dayjs from "dayjs";
import { Avatar, Button, Input, Table } from "antd";
import { SortingText } from "../../../utils/sortingData";

const NewLeagueGames = () => {
  const navigate = useNavigate();

  const { postLeagueGames } = LeagueGamesAPI;

  const [clubs, setClubs] = useState([]);
  const [totalItems, setTotalItems] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [keyWordSearch, setKeyWordSearch] = useState(null);
  const [searchClub, setSearchClub] = useState(null);
  const [clubsSelected, setClubsSelected] = useState({});

  const fetchClubs = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getClubs({ keyword: searchClub }, currentPage, 10);

      setClubs(response.results);
      setTotalItems(response.total);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchClub]);

  const handleSubmit = async (values) => {
    try {
      const result = {
        nameAr: values.nameAr,
        nameEn: values.nameEn,
        startTime: dayjs
          .tz(values.startTime, "YYYY-MM-DD HH:mm", "Asia/Riyadh")
          .utc()
          .format(),
        isEnable: values.isEnable,
        endTime: values.endTime
          ? dayjs
              .tz(values.endTime, "YYYY-MM-DD HH:mm", "Asia/Riyadh")
              .utc()
              .format()
          : null,
        clubs: [],
      };

      Object.keys(values).forEach((key) => {
        if (key.startsWith("isEnableClub-")) {
          const clubId = key.replace("isEnableClub-", "");
          result.clubs.push({
            clubId,
            metadata: {
              isEnable: values[key],
            },
          });
        }
      });

      console.log("🚀 ===== result:", result);
      const response = await postLeagueGames(result);

      if (response.status === 201) {
        navigate("/league-game");
      }
    } catch (error) {
      console.log("🚀 ===== error:", error);
    }
  };

  const tableColumns = [
    {
      key: "logo",
      title: "Logo",
      dataIndex: "logo",
      search: false,
      render: (_, record) => {
        return <Avatar src={record?.logo} />;
      },
    },
    {
      key: "nameEn",
      title: "English Name",
      dataIndex: "nameEn",
      search: false,
      sorter: (current, next) => SortingText(current.nameEn, next.nameEn),
    },
    {
      key: "nameAr",
      title: "Arabic Name",
      dataIndex: "nameAr",
      search: false,
      sorter: (current, next) => SortingText(current.nameAr, next.nameAr),
    },
  ];

  const onSelect = useCallback((item, isSelected) => {
    setClubsSelected((preValue) => {
      const result = JSON.parse(JSON.stringify(preValue ?? {}));
      if (isSelected) {
        result[item.id] = item;
      } else {
        delete result[item.id];
      }

      return result;
    });
  }, []);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: Object.keys(clubsSelected),
      onSelect: onSelect,
    }),
    [clubsSelected, onSelect]
  );

  const handleSubmitSearch = () => {
    setSearchClub(keyWordSearch);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  return (
    <PageContainer title="Create League Game">
      <ProForm name="settingForm" layout="horizontal" onFinish={handleSubmit}>
        <ProForm.Group>
          <div className="form-fields-wrapper">
            <ProFormText
              width="lg"
              label="Name AR"
              placeholder="Enter name AR"
              name="nameAr"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Name AR is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormText
              width="lg"
              label="Name EN"
              placeholder="Enter name EN"
              name="nameEn"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Name EN is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormDateTimePicker
              initialValue={new Date()}
              name="startTime"
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              width="lg"
              label="Start time"
              placeholder="Choose start time"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Start time is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormDateTimePicker
              name="endTime"
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              width="lg"
              label="End time"
              placeholder="Choose end time"
            />
            <ProFormSwitch name="isEnable" label="League Game Toggle" />
          </div>
          <div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Input
                style={{ marginBottom: "10px", width: "200px" }}
                placeholder="Search club"
                onChange={(e) => setKeyWordSearch(e.target.value)}
              />
              <Button htmlType="button" onClick={handleSubmitSearch}>
                Query
              </Button>
            </div>
            <Table
              loading={isLoading}
              rowSelection={rowSelection}
              size="small"
              style={{ marginBottom: "16px" }}
              rowKey="id"
              scroll={{ x: 1400, y: 500 }}
              columns={tableColumns}
              dataSource={clubs}
              pagination={{
                pageSize: 10,
                total: totalItems,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </div>

          {Object.values(clubsSelected).map((club) => (
            <ProFormSwitch
              key={club.id}
              name={`isEnableClub-${club.id}`}
              label={`Club ${club.nameEn} Enable`}
              initialValue={false}
            />
          ))}
        </ProForm.Group>
      </ProForm>
    </PageContainer>
  );
};

export default NewLeagueGames;
